import { useCallback } from 'react';
import { useUIStore } from '../../stores/useUIStore';
import { usePcfValidationStore } from '../../stores/usePcfValidationStore';

/**
 * Navigation hook that provides convenient access to navigation functionality
 * from the UIStore. Replaces Redux-based navigation.
 *
 * @example
 * ```typescript
 * const { navigateTo, goBack, currentScreen, canGoBack } = useNavigation();
 *
 * // Navigate to a screen
 * navigateTo('menu');
 *
 * // Go back
 * if (canGoBack) {
 *   goBack();
 * }
 *
 * // Check current screen
 * if (currentScreen === 'welcome') {
 *   // ...
 * }
 * ```
 */
export const useNavigation = () => {
  // State selectors
  const currentScreen = useUIStore((state) => state.screen);
  const navigationHistory = useUIStore((state) => state.navigationHistory);
  const canGoBack = navigationHistory.indexOf(currentScreen) > 0;

  // Action selectors
  const navigateTo = useUIStore((state) => state.navigateTo);
  const jumpTo = useUIStore((state) => state.jumpTo);
  const goBack = useUIStore((state) => state.goBack);
  const clearHistory = useUIStore((state) => state.clearNavigationHistory);

  // Convenience navigation methods
  const navigateToWelcome = useCallback(() => navigateTo('welcome'), [navigateTo]);
  const navigateToMenu = useCallback(() => navigateTo('menu'), [navigateTo]);
  const navigateToConnectorBinding = useCallback(() => navigateTo('connectorBinding'), [navigateTo]);
  const navigateToQuickFill = useCallback(() => navigateTo('quickFillProperties'), [navigateTo]);
  const navigateToBoltConnection = useCallback(() => navigateTo('boltConnection'), [navigateTo]);
  const navigateToProcessProperties = useCallback(() => navigateTo('processProperties'), [navigateTo]);
  const navigateToAdditionalCategories = useCallback(() => navigateTo('additionalCategories'), [navigateTo]);
  const navigateToMappingConfiguration = useCallback(() => navigateTo('mappingConfiguration'), [navigateTo]);

  // Export has no Screen of its own; it is tab 3 of the mapping workspace, so
  // reaching it means requesting the workspace tab and landing on its screen.
  const requestWorkspaceTab = usePcfValidationStore((state) => state.requestWorkspaceTab);
  const navigateToExport = useCallback(() => {
    requestWorkspaceTab('export');
    navigateTo('mappingConfiguration');
  }, [navigateTo, requestWorkspaceTab]);

  return {
    // State
    currentScreen,
    navigationHistory,
    canGoBack,

    // Actions
    navigateTo,
    jumpTo,
    goBack,
    clearHistory,

    // Convenience methods
    navigateToWelcome,
    navigateToMenu,
    navigateToConnectorBinding,
    navigateToQuickFill,
    navigateToBoltConnection,
    navigateToProcessProperties,
    navigateToAdditionalCategories,
    navigateToMappingConfiguration,
    navigateToExport,
  };
};

/**
 * Hook to get just the current screen (optimized selector)
 */
export const useCurrentScreen = () => useUIStore((state) => state.screen);

/**
 * Hook to get navigation history (optimized selector)
 */
export const useNavigationHistory = () => useUIStore((state) => state.navigationHistory);

/**
 * Hook to get selected option (optimized selector)
 */
export const useSelectedOption = () => useUIStore((state) => state.selectedOption);
